import React from 'react';
import { humanizeJsonKey, parseAgentMessageBlocks, parseBoldSegments } from '@/utils/agentMessageFormatting';
import MarkdownRenderer from '@stepflow/ui-shared/components/MarkdownRenderer.jsx';

function primitiveToText(value) {
  if (value == null) return 'Not specified';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  return String(value);
}

function isPrimitive(value) {
  return value == null || ['string', 'number', 'boolean'].includes(typeof value);
}

function InlineText({ text }) {
  return (
    <>
      {parseBoldSegments(text).map((segment, idx) => (
        segment.bold
          ? <strong key={idx} className="font-semibold text-slate-950">{segment.text}</strong>
          : <React.Fragment key={idx}>{segment.text}</React.Fragment>
      ))}
    </>
  );
}

function JsonValue({ value, depth = 0 }) {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-slate-400">None</span>;
    }
    if (value.every(isPrimitive)) {
      return (
        <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-slate-800 marker:text-slate-400">
          {value.map((item, idx) => (
            <li key={`${idx}-${primitiveToText(item)}`} className="leading-relaxed">
              <InlineText text={primitiveToText(item)} />
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div className="mt-1 space-y-3">
        {value.map((item, idx) => (
          <div key={idx} className="pl-0 sm:pl-1">
            <JsonValue value={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return <span className="text-slate-400">No details</span>;
    }
    const indent = depth > 0 ? ' pl-2 sm:pl-3' : '';
    return (
      <div className={`${depth === 0 ? 'space-y-3' : 'mt-0.5 space-y-2.5'}${indent}`}>
        {entries.map(([key, child]) => (
          <div key={key} className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              {humanizeJsonKey(key)}
            </div>
            <div className="mt-0.5 text-inherit leading-relaxed text-slate-800">
              <JsonValue value={child} depth={depth + 1} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const text = primitiveToText(value);
  if (typeof value === 'string' && (text.includes('\n') || text.length > 220)) {
    return (
      <div className="whitespace-pre-wrap leading-relaxed text-slate-800">
        <InlineText text={text} />
      </div>
    );
  }
  return (
    <span className={typeof value === 'string' && text.length > 120 ? 'leading-relaxed' : ''}>
      <InlineText text={text} />
    </span>
  );
}

function JsonBlock({ value }) {
  return (
    <div className="min-w-0">
      <JsonValue value={value} />
    </div>
  );
}

function CodeBlock({ block }) {
  // Horizontal overflow ONLY — no inner vertical scroll (was ``max-h-80 overflow-auto``, which gave every
  // code block its own vertical scrollbar → scrollbars-inside-scrollbars when many blocks stack in a long
  // output). The reader panel owns the single vertical scroll; code just flows + scrolls sideways for
  // long lines. (L53)
  return (
    <pre className="overflow-x-auto text-xs font-mono leading-relaxed text-slate-700">
      <code>{block.text}</code>
    </pre>
  );
}

/** One rendered block. Memoized on its OWN content so a stable (already-streamed) block never
 *  re-renders when later text arrives — the fix for the "everything reflows on every chunk"
 *  instability. Keyed by a content-derived key by the parent, not by array index. */
const MessageBlock = React.memo(function MessageBlock({ block }) {
  if (block.type === 'json') return <JsonBlock value={block.value} />;
  if (block.type === 'code') return <CodeBlock block={block} />;
  return <MarkdownRenderer content={block.text} theme="light" />;
});

export default function AgentMessageContent({ text, className = '', streaming: _streaming = false }) {
  const blocks = parseAgentMessageBlocks(text);
  if (blocks.length === 0) return null;

  // KEY STABILITY across the streaming→done handoff (the "text jumps out then back in" bug):
  // every block is keyed by INDEX (``blk:${idx}``) — NOT by content and NOT gated on ``streaming``.
  // The tail block's text changes every chunk AND at stream-end (the ``streaming`` prop flips
  // true→false), but its key stays constant, so React UPDATES the same DOM node in place instead
  // of unmounting+remounting it. The old key flipped ``live:idx`` → content-key when streaming
  // ended → React saw a new key → tore the block out (height collapses, page jumps up) and
  // re-mounted it (jumps back). Index keys keep the same elements throughout; React.memo still
  // skips re-rendering the frozen earlier blocks (block order only grows by appending).
  return (
    <div className={`mt-1 space-y-3 ${className}`.trim()}>
      {blocks.map((block, idx) => (
        <MessageBlock key={`blk:${idx}`} block={block} />
      ))}
    </div>
  );
}
